import React, { useEffect, useState, useCallback } from "react";
import { 
  FaRuler, FaTimes, FaDownload, FaChevronLeft, FaChevronRight, 
  FaCheckCircle, FaMousePointer,
  FaArrowRight, FaCheck, FaUndo, FaCrosshairs,
  FaExclamationTriangle
} from "react-icons/fa";
import "./ZonageCotationsPanel.css";

const ZonageCotationsPanel = ({ 
  selectedZonage, 
  onClose,
  zonagesDetails,
  onSelectModeChange,
  onSideSelected,
  onPolygonSelect
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('zonage');
  const [selectedPolygon, setSelectedPolygon] = useState(null);
  const [polygonMeasurements, setPolygonMeasurements] = useState([]);
  const [zonagePolygons, setZonagePolygons] = useState([]);
  
  // ÉTATS POUR LA SÉLECTION MANUELLE
  const [selectedSides, setSelectedSides] = useState([]);
  const [currentSideIndex, setCurrentSideIndex] = useState(1);
  const [selectionStatus, setSelectionStatus] = useState('idle');
  const [currentPoints, setCurrentPoints] = useState([]);
  // 🎯 CALCUL DE DISTANCE PRÉCIS
  const calculateDistanceInMeters = useCallback((point1, point2) => {
    try {
      const [lon1, lat1] = point1;
      const [lon2, lat2] = point2;
      
      const R = 6371000;
      const dLat = (lat2 - lat1) * Math.PI / 180;
      const dLon = (lon2 - lon1) * Math.PI / 180;
      
      const a = 
        Math.sin(dLat/2) * Math.sin(dLat/2) +
        Math.cos(lat1 * Math.PI / 180) * 
        Math.cos(lat2 * Math.PI / 180) * 
        Math.sin(dLon/2) * Math.sin(dLon/2);
      
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
      const distance = R * c;
      
      return Math.round(distance * 100) / 100;
    } catch (err) {
      console.error("❌ Erreur calcul distance:", err);
      return 0;
    }
  }, []);

const handlePolygonClick = useCallback((polygon) => {
  console.log("🟢 Clic sur polygone dans le panneau:", polygon);
  
  setLoading(true);
  setSelectedPolygon(polygon);
  setSelectedSides([]);
  setCurrentSideIndex(1);
  setSelectionStatus('idle');
  setCurrentPoints([]);

  if (onPolygonSelect) {
    console.log("🔥 Envoi des données de sélection à la carte depuis le panneau");
    onPolygonSelect({
      polygon: polygon,
      isSelected: true,
      zonageGid: selectedZonage.zonage_gid,
      polygonIndex: polygon.index  
    });
  }
  
  try {
    const geometry = JSON.parse(polygon.geom_geojson);
    let coordinates = geometry.coordinates[0];
    
    console.log(`🔍 Polygone sélectionné: ${coordinates.length} points`);
    
    const pourcentageSurface = polygon.superficie > 0 
      ? (polygon.surface_exacte / polygon.superficie * 100)
      : 0;

    setPolygonMeasurements([{
      surface_exacte: polygon.surface_exacte,
      superficie: polygon.superficie,
      pourcentage_surface: pourcentageSurface,
      type_zonage: polygon.type_zonage,
      zonage_gid: polygon.zonage_gid,
      polygoneIndex: polygon.index,
      perimeter: 0,
      segments: [],
      vertexCount: 0,
      originalVertexCount: coordinates.length
    }]);

    setViewMode('polygone');
    
  } catch (error) {
    console.error("❌ Erreur analyse polygone:", error);
    setError("Erreur lors du chargement du polygone: " + error.message);
  } finally {
    setLoading(false);
  }
}, [selectedZonage, onPolygonSelect]);

  const startManualSelection = useCallback(() => {
    console.log("🎯 Démarrage de la sélection manuelle");
    
    setSelectedSides([]);
    setCurrentSideIndex(1);
    setSelectionStatus('selecting_first_point');
    setCurrentPoints([]);
    

    if (onSelectModeChange) {
      onSelectModeChange({
        active: true,
        mode: 'manual_points',
        currentSide: 1,
        polygon: selectedPolygon,
        status: 'selecting_first_point',
        disablePopups: true,
        message: "Cliquez sur le PREMIER point du côté 1"
      });
    }
    
    console.log("🖱️ Mode sélection manuelle activé - Prêt pour le premier point");
  }, [selectedPolygon, onSelectModeChange]);

  const resetSelection = useCallback(() => {
    console.log("🔄 Réinitialisation de la sélection");
    
    setSelectedSides([]);
    setCurrentSideIndex(1);
    setSelectionStatus('idle');
    setCurrentPoints([]);
    

    if (onSelectModeChange) {
      onSelectModeChange({ 
        active: false,
        mode: null,
        status: 'idle',
        disablePopups: false
      });
    }

    if (selectedPolygon) {
      const pourcentageSurface = selectedPolygon.superficie > 0 
        ? (selectedPolygon.surface_exacte / selectedPolygon.superficie * 100)
        : 0;

      setPolygonMeasurements([{
        surface_exacte: selectedPolygon.surface_exacte,
        superficie: selectedPolygon.superficie,
        pourcentage_surface: pourcentageSurface,
        type_zonage: selectedPolygon.type_zonage,
        zonage_gid: selectedPolygon.zonage_gid,
        polygoneIndex: selectedPolygon.index,
        perimeter: 0,
        segments: [],
        vertexCount: 0,
        originalVertexCount: selectedPolygon.originalVertexCount
      }]);
    }
  }, [selectedPolygon, onSelectModeChange]);

  const handlePointFromMap = useCallback((point) => {
    console.log("📍 Point reçu de la carte:", point, "Statut actuel:", selectionStatus);
    
    if (!point || !Array.isArray(point) || point.length !== 2) {
      console.error("❌ Point invalide reçu:", point);
      return;
    }

    if (selectionStatus === 'selecting_first_point') {
      console.log("✅ Premier point sélectionné:", point);
      setCurrentPoints([point]);
      setSelectionStatus('selecting_second_point');
      
      if (onSelectModeChange) {
        onSelectModeChange({
          active: true,
          mode: 'manual_points',
          currentSide: currentSideIndex,
          polygon: selectedPolygon,
          status: 'selecting_second_point',
          disablePopups: true,
          message: `Cliquez sur le DEUXIÈME point du côté ${currentSideIndex}`
        });
      }
    }

    else if (selectionStatus === 'selecting_second_point' && currentPoints.length === 1) {
      console.log("✅ Deuxième point sélectionné:", point);
      
      const pointA = currentPoints[0];
      const pointB = point;
      const distance = calculateDistanceInMeters(pointA, pointB);
      
      console.log(`📏 Distance calculée: ${distance}m`);
      
      const newSide = {
        pointA: pointA,
        pointB: pointB,
        distance: distance,
        segment: currentSideIndex
      };

      const newSides = [...selectedSides, newSide];
      setSelectedSides(newSides);
      
      updateMeasurementsWithSides(newSides);
      
      setSelectionStatus('measured');
      setCurrentPoints([]);
      
      console.log(`✅ Côté ${currentSideIndex} mesuré: ${distance.toFixed(2)} m`);
      
      // Informer la carte du côté mesuré
      if (onSelectModeChange) {
        onSelectModeChange({
          active: true,
          mode: 'manual_points',
          currentSide: currentSideIndex,
          polygon: selectedPolygon,
          status: 'measured',
          disablePopups: true,
          message: `Côté ${currentSideIndex} mesuré: ${distance.toFixed(2)} m`
        });
      }
    }
  }, [selectionStatus, currentPoints, currentSideIndex, selectedPolygon, selectedSides, onSelectModeChange, calculateDistanceInMeters]);

  useEffect(() => {
    console.log("🎯 Configuration de la réception des points depuis la carte");
    
    if (onSideSelected && typeof onSideSelected === 'function') {
      console.log("✅ Configuration du callback handlePointFromMap");
      onSideSelected(handlePointFromMap);
    } else {
      console.error("❌ onSideSelected n'est pas une fonction valide:", onSideSelected);
    }

    return () => {
      if (onSideSelected && typeof onSideSelected === 'function') {
        console.log("🧹 Nettoyage du callback");
        onSideSelected(null);
      }
    };
  }, [onSideSelected, handlePointFromMap]);

  const updateMeasurementsWithSides = useCallback((sides) => {
    if (!selectedPolygon || sides.length === 0) return;

    const segmentMeasurements = sides.map((side, index) => ({
      segment: index + 1,
      distance: side.distance,
      pointA: side.pointA,
      pointB: side.pointB,
      isManual: true
    }));

    const totalPerimeter = sides.reduce((sum, side) => sum + side.distance, 0);

    const pourcentageSurface = selectedPolygon.superficie > 0 
      ? (selectedPolygon.surface_exacte / selectedPolygon.superficie * 100)
      : 0;

    setPolygonMeasurements([{
      surface_exacte: selectedPolygon.surface_exacte,
      superficie: selectedPolygon.superficie,
      pourcentage_surface: pourcentageSurface,
      type_zonage: selectedPolygon.type_zonage,
      zonage_gid: selectedPolygon.zonage_gid,
      polygoneIndex: selectedPolygon.index,
      perimeter: totalPerimeter,
      segments: segmentMeasurements,
      vertexCount: segmentMeasurements.length,
      originalVertexCount: selectedPolygon.originalVertexCount
    }]);
  }, [selectedPolygon]);

  const nextSide = useCallback(() => {
    const nextIndex = currentSideIndex + 1;
    setCurrentSideIndex(nextIndex);
    setSelectionStatus('selecting_first_point');
    setCurrentPoints([]);
    
    if (onSelectModeChange) {
      onSelectModeChange({
        active: true,
        mode: 'manual_points',
        currentSide: nextIndex,
        polygon: selectedPolygon,
        status: 'selecting_first_point',
        disablePopups: true,
        message: `Cliquez sur le PREMIER point du côté ${nextIndex}`
      });
    }
    
    console.log(`➡️ Prêt pour la sélection du côté ${nextIndex}`);
  }, [currentSideIndex, selectedPolygon, onSelectModeChange]);

  const finishSelection = useCallback(() => {
    setSelectionStatus('completed');
    
    if (onSelectModeChange) {
      onSelectModeChange({
        active: false,
        mode: null,
        status: 'completed',
        disablePopups: false,
        message: "Sélection terminée"
      });
    }
    
    console.log("✅ Sélection manuelle terminée");
  }, [onSelectModeChange]);


  const removeSide = useCallback((segmentIndex) => {
    console.log("🗑️ Suppression du côté:", segmentIndex);
    
    const newSides = selectedSides.filter(side => side.segment !== segmentIndex);
    const renumberedSides = newSides.map((side, index) => ({
      ...side,
      segment: index + 1
    }));

    setSelectedSides(renumberedSides);
    setCurrentSideIndex(renumberedSides.length + 1);

    if (renumberedSides.length > 0) {
      updateMeasurementsWithSides(renumberedSides);
      setSelectionStatus('selecting_first_point');
      
      if (onSelectModeChange) {
        onSelectModeChange({
          active: true,
          mode: 'manual_points',
          currentSide: renumberedSides.length + 1,
          polygon: selectedPolygon,
          status: 'selecting_first_point',
          disablePopups: true,
          message: `Cliquez sur le PREMIER point du côté ${renumberedSides.length + 1}`
        });
      }
    } else {
      resetSelection();
    }
  }, [selectedSides, selectedPolygon, updateMeasurementsWithSides, onSelectModeChange, resetSelection]);

  useEffect(() => {
    if (!selectedZonage || !selectedZonage.zonage_gid) {
      setZonagePolygons([]);
      setPolygonMeasurements([]);
      setSelectedPolygon(null);
      setViewMode('zonage');
      setError(null);
      setSelectionStatus('idle');
      setCurrentPoints([]);
      
      if (onPolygonSelect) {
        onPolygonSelect({ isSelected: false });
      }
      return;
    }
    
    setLoading(true);
    setError(null);
    setViewMode('zonage');
    setSelectedPolygon(null);
    setSelectionStatus('idle');
    setCurrentPoints([]);
    
    try {
      const details = findZonageDetails(selectedZonage, zonagesDetails);
      
      if (details && details.polygones_individualises) {
        const polygons = details.polygones_individualises.map((poly, index) => ({
          index: index,
          surface_exacte: Number(poly.surface_intersection_individuelle_m2 || 0),
          superficie: Number(poly.surf_adop || 0),
          type_zonage: poly.type_zonage,
          zonage_gid: poly.zonage_gid,
          geom_geojson: poly.geom_geojson,
          originalVertexCount: JSON.parse(poly.geom_geojson)?.coordinates[0]?.length || 0
        }));
        setZonagePolygons(polygons);
      } else {
        setError("Aucun polygone trouvé dans ce zonage");
        setZonagePolygons([]);
      }
    } catch (err) {
      console.error("❌ Erreur:", err);
      setError("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  }, [selectedZonage, zonagesDetails, onPolygonSelect]);

  const findZonageDetails = (selectedZonage, zonagesDetails) => {
    if (!zonagesDetails) return null;
    const possibleKeys = [
      selectedZonage.zonage_gid,
      `${selectedZonage.titreGid}-${selectedZonage.zonage_gid}`,
      selectedZonage.titreGid?.toString(),
    ];
    for (const key of possibleKeys) {
      if (zonagesDetails[key]) {
        return zonagesDetails[key];
      }
    }
    return null;
  };

  const handleBackToZonage = useCallback(() => {
    setViewMode('zonage');
    setSelectedPolygon(null);
    setPolygonMeasurements([]);
    setSelectedSides([]);
    setSelectionStatus('idle');
    setCurrentPoints([]);
    
    if (onPolygonSelect) {
      onPolygonSelect({ isSelected: false });
    }
    
    if (onSelectModeChange) {
      onSelectModeChange({ 
        active: false,
        disablePopups: false 
      });
    }
  }, [onPolygonSelect, onSelectModeChange]);

  const handleExport = useCallback(() => {
    if (!selectedPolygon || polygonMeasurements.length === 0) return;

    try {
      const polyMeasure = polygonMeasurements[0];
      let textContent = `RAPPORT DE MESURES EXACTES\n`;
      textContent += `Mode: SÉLECTION MANUELLE\n`;
      textContent += `================================\n\n`;
      
      textContent += `INFORMATIONS OFFICIELLES\n`;
      textContent += `-----------------------\n`;
      textContent += `Type de zonage: ${polyMeasure.type_zonage}\n`;
      textContent += `Surface exacte: ${polyMeasure.surface_exacte.toFixed(2)} m²\n`;
      textContent += `Superficie totale: ${polyMeasure.superficie.toFixed(2)} m²\n`;
      textContent += `Pourcentage: ${polyMeasure.pourcentage_surface.toFixed(1)}%\n\n`;
      
      textContent += `MESURES GÉOMÉTRIQUES\n`;
      textContent += `-------------------\n`;
      textContent += `Périmètre total: ${polyMeasure.perimeter.toFixed(2)} m\n`;
      textContent += `Nombre de côtés: ${polyMeasure.vertexCount}\n`;
      textContent += `Points d'origine: ${polyMeasure.originalVertexCount}\n\n`;
      
      textContent += `MESURES DES CÔTÉS (MANUELLES)\n`;
      textContent += `---------------------------\n`;
      
      if (polyMeasure.segments.length > 0) {
        polyMeasure.segments.forEach((segment) => {
          textContent += `Côté ${segment.segment}: ${segment.distance.toFixed(2)} m\n`;
        });
      } else {
        textContent += `Aucun côté mesuré\n`;
      }

      textContent += `\nRapport généré le: ${new Date().toLocaleString('fr-FR')}`;

      const blob = new Blob([textContent], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `mesures_${polyMeasure.type_zonage}_manuel_${Date.now()}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

    } catch (error) {
      console.error("❌ Erreur export:", error);
      alert("Erreur lors de l'exportation");
    }
  }, [selectedPolygon, polygonMeasurements]);

  if (!selectedZonage) {
    return null;
  }

  return (
    <div className={`side-panel ${isExpanded ? 'expanded' : 'collapsed'}`}>
      <button 
        className="panel-toggle"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        {isExpanded ? <FaChevronRight /> : <FaChevronLeft />}
      </button>

      {isExpanded && (
        <div className="panel-content">
          <div className="panel-header">
            <div className="header-title">
              {viewMode === 'polygone' && (
                <button className="back-btn" onClick={handleBackToZonage}>
                  <FaChevronLeft />
                </button>
              )}
              <FaRuler className="header-icon" />
              <div className="title-text">
                <h2>
                  {viewMode === 'zonage' 
                    ? selectedZonage.type_zonage
                    : `Polygone ${selectedPolygon?.index + 1}`
                  }
                </h2>
                <span className="subtitle">
                  {viewMode === 'zonage' ? 'Zonage' : 'Mesures manuelles'}
                </span>
              </div>
            </div>
            <button className="close-btn" onClick={onClose}>
              <FaTimes />
            </button>
          </div>

          <div className="panel-body">
            {loading && (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>Chargement en cours...</p>
              </div>
            )}

            {error && (
              <div className="error-state">
                <p>{error}</p>
              </div>
            )}

            {!loading && !error && viewMode === 'zonage' && (
              <div className="zonage-view">
                <div className="summary-card">
                  <h3>Informations du zonage</h3>
                  <div className="summary-grid">
                    <div className="summary-item">
                      <span className="label">Type</span>
                      <span className="value">{selectedZonage.type_zonage}</span>
                    </div>
                    <div className="summary-item">
                      <span className="label">Surface totale</span>
                      <span className="value">
                        {Number(selectedZonage.surface_intersection_totale_m2 || 0).toFixed(2)} m²
                      </span>
                    </div>
                    <div className="summary-item">
                      <span className="label">Intersection</span>
                      <span className="value">
                        {Number(selectedZonage.pourcentage_intersection || 0).toFixed(2)}%
                      </span>
                    </div>
                    <div className="summary-item">
                      <span className="label">Polygones</span>
                      <span className="value">{zonagePolygons.length}</span>
                    </div>
                  </div>
                </div>

                {zonagePolygons.length > 0 && (
                  <div className="polygons-section">
                    <h3>Polygones disponibles</h3>
                    <div className="polygons-grid">
                      {zonagePolygons.map((polygon, index) => (
                        <div 
                          key={index} 
                          className="polygon-card"
                          onClick={() => handlePolygonClick(polygon)}
                        >
                          <div className="polygon-header">
                            <span className="polygon-number">#{index + 1}</span>
                            <FaCheckCircle className="verified-icon" />
                          </div>
                          <div className="polygon-content">
                            <span className="surface">
                              {polygon.surface_exacte.toFixed(2)} m²
                            </span>
                            <span className="type">{polygon.type_zonage}</span>
                          </div>
                          <div className="polygon-hint">
                            Cliquer pour mesurer les cotations
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {!loading && !error && viewMode === 'polygone' && polygonMeasurements.length > 0 && (
              <div className="polygon-view">
                <div className="polygon-header-card">
                  <div className="polygon-title">
                    <h3>Polygone #{selectedPolygon.index + 1}</h3>
                    <span className="zonage-type">{polygonMeasurements[0].type_zonage}</span>
                  </div>

                  {/* CONTROLES DE SÉLECTION MANUELLE */}
                  <div className="selection-controls">
                    <h4>Mesure manuelle des côtés</h4>
                    
                    {selectionStatus === 'idle' && (
                      <div className="start-measurement-section">
                        <p>Pour mesurer ce polygone, cliquez sur le bouton ci-dessous et sélectionnez les points sur la carte.</p>
                        <button 
                          className="start-measurement-btn"
                          onClick={startManualSelection}
                        >
                          <FaMousePointer /> Commencer la mesure manuelle
                        </button>
                      </div>
                    )}

                    {selectionStatus !== 'idle' && (
                      <div className="manual-selection-process">
                        <div className="selection-status-card">
                          <div className="status-header">
                            <FaCrosshairs className="status-icon" />
                            <h4>Sélection manuelle active</h4>
                            <div className="popup-warning">
                              <FaExclamationTriangle />
                
                            </div>
                          </div>
                          
                          <div className="status-content">
                            {selectionStatus === 'selecting_first_point' && (
                              <div className="status-step active">
                                <div className="step-indicator">1</div>
                                <div className="step-info">
                                  <p><strong>Cliquez sur le PREMIER point du côté #{currentSideIndex}</strong></p>
                                  <p className="step-hint">
                                    Sélectionnez le point de départ du côté sur le polygone
                                  </p>
                                </div>
                              </div>
                            )}

                            {selectionStatus === 'selecting_second_point' && (
                              <div className="status-step active">
                                <div className="step-indicator">2</div>
                                <div className="step-info">
                                  <p><strong>Cliquez sur le DEUXIÈME point du côté #{currentSideIndex}</strong></p>
                                  <p className="step-hint">
                                    Sélectionnez l'extrémité du côté sur le polygone
                                  </p>
                                  {currentPoints.length > 0 && (
                                    <p className="point-selected">
                                      ✓ Premier point sélectionné
                                    </p>
                                  )}
                                </div>
                              </div>
                            )}

                            {selectionStatus === 'measured' && (
                              <div className="status-step completed">
                                <div className="step-indicator">✓</div>
                                <div className="step-info">
                                  <p><strong>Côté #{currentSideIndex} mesuré !</strong></p>
                                  <p className="distance-result">
                                    Distance: <strong>{selectedSides[selectedSides.length - 1]?.distance?.toFixed(2) || '0.00'} m</strong>
                                  </p>
                                  <div className="step-actions">
                                    <button className="btn-next" onClick={nextSide}>
                                      <FaArrowRight /> Côté suivant
                                    </button>
                                    <button className="btn-finish" onClick={finishSelection}>
                                      <FaCheck /> Terminer
                                    </button>
                                  </div>
                                </div>
                              </div>
                            )}

                            {selectionStatus === 'completed' && (
                              <div className="status-step completed">
                                <div className="step-indicator">✓</div>
                                <div className="step-info">
                                  <p><strong>Sélection terminée !</strong></p>
                                  <p>{selectedSides.length} côtés mesurés manuellement</p>
                                  <p className="step-hint">Les popups sont maintenant réactivés</p>
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="selection-progress">
                            <div className="progress-info">
                              <span>Côtés mesurés: <strong>{selectedSides.length}</strong></span>
                              <span>En cours: <strong>#{currentSideIndex}</strong></span>
                              {currentPoints.length > 0 && (
                                <span>Points sélectionnés: <strong>{currentPoints.length}/2</strong></span>
                              )}
                            </div>
                            {selectedSides.length > 0 && (
                              <button className="btn-reset" onClick={resetSelection}>
                                <FaUndo /> Recommencer
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="exact-data-section">
                    <div className="data-card">
                      <h4>Surface exacte</h4>
                      <div className="data-value highlight">
                        {polygonMeasurements[0].surface_exacte.toFixed(2)} m²
                      </div>
                    </div>
                    <div className="data-card">
                      <h4>Pourcentage</h4>
                      <div className="data-value">
                        {polygonMeasurements[0].pourcentage_surface.toFixed(1)}%
                      </div>
                    </div>
                  </div>
                </div>

                <div className="sides-section">
                  <h4>
                    Mesures des côtés ({polygonMeasurements[0].vertexCount} côtés mesurés manuellement)
                  </h4>
                  {polygonMeasurements[0].segments.length > 0 ? (
                    <div className="sides-list">
                      {polygonMeasurements[0].segments.map((segment, segIndex) => (
                        <div key={segIndex} className="side-item manual-side">
                          <div className="side-header">
                            <span className="side-name">
                              Côté {segment.segment}
                              <span className="manual-badge">Manuel</span>
                            </span>
                            <span className="side-distance">{segment.distance.toFixed(2)} m</span>
                          </div>
                          <div className="side-actions">
                            <button 
                              className="remove-side-btn"
                              onClick={() => removeSide(segment.segment)}
                            >
                              × Supprimer
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="no-sides-message">
                      <p>Aucun côté mesuré. Cliquez sur "Commencer la mesure manuelle" pour démarrer.</p>
                    </div>
                  )}
                </div>
                <div className="actions-section">
                  <button className="export-button" onClick={handleExport} disabled={polygonMeasurements[0].segments.length === 0}>
                    <FaDownload />
                    Exporter le rapport
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ZonageCotationsPanel;